import { useQuery } from '@tanstack/react-query';
import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/series-label';
import HighchartsReact from 'highcharts-react-official';
import type { TFunction } from 'i18next';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { MappedAssetSensor } from '@/common/asset-sensor';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/context/ThemeContext';
import { HIGH_CHART_DARK_THEME, HIGH_CHART_LIGHT_THEME } from '@/lib/highchart/setup';
import { useQueryGetMetTimeSeriesChart } from '@/queries/assets/sensors/met/get-timeseries-chart';
import {
	type TpsSensorTimeSeriesChartFilters,
	useQueryGetTpsTimeSeriesChart,
} from '@/queries/assets/sensors/tps/get-timeseries-chart';
import { AbsoluteLoader } from '../../../absoluteLoader';
import { EmptyChart } from '../../EmptyChart';

function baseChartOptions(titleText: string, yAxisTitle: string, t: TFunction): Highcharts.Options {
	return {
		chart: {
			zooming: {
				type: 'x',
			},
			height: 300,
		},
		title: { text: titleText, style: { fontSize: '14px' } },
		xAxis: { type: 'datetime' },
		legend: {
			enabled: false,
		},
		yAxis: [{ title: { text: yAxisTitle } }, { title: { text: t('health.charts.temperatureUnit') }, opposite: true }],
		tooltip: { shared: true },
	};
}

export type TpsTimeSeriesVectorChartsFilters = {
	startDate: Date | undefined;
	endDate: Date | undefined;
	meteoSensor: string;
	aggregation: TpsSensorTimeSeriesChartFilters['aggregation'] | 'none';
	geglattet: boolean;
	delta: boolean;
};

export type TpsTimeSeriesVectorChartsProps = {
	selectedSensor: MappedAssetSensor;
	assetId: string;
	filters: TpsTimeSeriesVectorChartsFilters;
};

export function TpsTimeSeriesVectorCharts({ selectedSensor, assetId, filters }: TpsTimeSeriesVectorChartsProps) {
	const { t } = useTranslation();
	const { isDarkTheme } = useTheme();
	const chartFilters: TpsSensorTimeSeriesChartFilters = useMemo(
		() => ({
			from: filters.startDate?.toISOString(),
			to: filters.endDate?.toISOString(),
			aggregation: filters.aggregation !== 'none' ? filters.aggregation : undefined,
			smooth: filters.geglattet,
			delta: filters.delta,
		}),
		[filters.startDate, filters.endDate, filters.aggregation, filters.geglattet, filters.delta],
	);
	const meteoChartFilters = useMemo(
		() => ({
			from: filters.startDate?.toISOString(),
			to: filters.endDate?.toISOString(),
			aggregation: filters.aggregation !== 'none' ? filters.aggregation : undefined,
		}),
		[filters.startDate, filters.endDate, filters.aggregation],
	);
	const { data: chartResponse, isLoading } = useQuery(
		useQueryGetTpsTimeSeriesChart(assetId, selectedSensor.dataLoggerId, selectedSensor.id, chartFilters),
	);
	const { data: meteoChartResponse } = useQuery(
		useQueryGetMetTimeSeriesChart(assetId, filters.meteoSensor ? Number(filters.meteoSensor) : null, meteoChartFilters),
	);
	const tempData = meteoChartResponse?.chartData || [];
	const chartData = chartResponse?.chartData || [];
	const chartFinalData = useMemo(() => {
		const r2ChartData = chartData.map(
			(record) =>
				[new Date(record.measGroup).getTime(), Math.sqrt(record.x * record.x + record.y * record.y)] as [
					number,
					number,
				],
		);
		const r3ChartData = chartData.map(
			(record) =>
				[
					new Date(record.measGroup).getTime(),
					Math.sqrt(record.x * record.x + record.y * record.y + record.z * record.z),
				] as [number, number],
		);
		const tempSeriesData = tempData.map(
			(record) => [new Date(record.measGroup).getTime(), record.t] as [number, number],
		);

		const yAxisTitle = filters.delta ? t('health.charts.change') : t('health.charts.coordinateMagnitude');
		const tempSeries = {
			name: t('health.charts.temperature'),
			data: tempSeriesData,
			type: 'line',
			color: '#ff7f7f',
			yAxis: 1,
			tooltip: { valueSuffix: ' °C' },
			visible: tempSeriesData.length > 0,
			zIndex: 3,
		};

		return [
			{
				key: 'r2',
				baseOptions: baseChartOptions(t('health.charts.vector2d'), yAxisTitle, t),
				series: [
					{ name: 'r₂', data: r2ChartData, type: 'line', zIndex: 5 },
					tempSeries,
				] as Highcharts.SeriesOptionsType[],
			},
			{
				key: 'r3',
				baseOptions: baseChartOptions(t('health.charts.vector3d'), yAxisTitle, t),
				series: [{ name: 'r₃', data: r3ChartData, type: 'line', zIndex: 5 }, tempSeries],
			},
		];
	}, [chartData, filters.delta, tempData, t]);
	const charts = useMemo(
		() =>
			chartFinalData.map((data) => ({
				...data,
				baseOptions: merge(
					structuredClone(isDarkTheme ? HIGH_CHART_DARK_THEME : HIGH_CHART_LIGHT_THEME),
					data.baseOptions,
				),
			})),
		[chartFinalData, isDarkTheme],
	);
	if (isLoading) {
		return (
			<div className="h-50 relative w-full">
				<AbsoluteLoader className="bg-transparent" isLoading />
			</div>
		);
	}
	if (!chartData?.length) {
		return <EmptyChart title={t('health.charts.noDataTitle')} subtitle={t('health.charts.noDataSubtitle')} />;
	}
	return (
		<div className="space-y-3">
			{charts.map((chart) => (
				<Card key={chart.key} className="border-zinc-300 py-0">
					<CardContent className="p-1">
						<HighchartsReact
							highcharts={Highcharts}
							options={{
								...chart.baseOptions,
								series: chart.series,
							}}
						/>
					</CardContent>
				</Card>
			))}
		</div>
	);
}
